import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { SendMessagePayload } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

interface UseSendMessageOptions {
  conversationId: ConversationId;
}

/**
 * Hook to send a message.
 * Optimistically marks the draft as SENDING in the collection so the
 * UI immediately reflects the in-flight state. The real message arrives via
 * the SSE messageCreated event, which triggers a collection invalidation.
 */
export function useSendMessage({ conversationId }: UseSendMessageOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalConversationCollections();
  const [isSending, setIsSending] = useState(false);

  const sendMessage = async (input: SendMessagePayload) => {
    setIsSending(true);
    try {
      collections.drafts.utils.writeUpdate({
        id: input.draftId,
        status: "SENDING" as const,
      });

      const client = effectApi.getClient();
      const sendCall = client.conversation.sendMessage({
        path: { id: conversationId },
        payload: input,
      });
      await effectApi.runPromise(catchApiErrors(sendCall));
    } finally {
      setIsSending(false);
    }
  };

  return { sendMessage, isSending };
}
